import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  Award,
  Lock,
  CheckCircle2,
  Waves,
  ArrowRight,
  GraduationCap,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

// Foto oficial do vencedor Carlos Eduardo Gerum Carraro Cruz
const WINNER_PHOTO = "/images/winner-carlos.jpg";

const LOTTERY_OFFICIAL_URL = "https://loterias.caixa.gov.br/paginas/federal.aspx";

/**
 * Animated counter from 0 → target when in view.
 */
function AnimatedCounter({ target = 1, duration = 1400, label, testId }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const startedAt = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - startedAt) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center rounded-2xl border border-[#F5C542]/25 bg-white/[0.03] px-6 py-5 backdrop-blur-sm sm:px-8 sm:py-6"
      data-testid={testId}
    >
      <span className="font-display text-4xl font-black leading-none text-[#F5C542] sm:text-5xl">
        {value}
      </span>
      <span className="mt-2 text-center text-[10px] font-bold uppercase tracking-widest text-white/70 sm:text-xs">
        {label}
      </span>
    </div>
  );
}

/**
 * Full "Winner" section — reusable across pages.
 *
 * @param {Object} props
 * @param {() => void} [props.onCta] Optional handler; default: scroll to #combos or #sorteio
 * @param {string} [props.regulamentoAnchor] Anchor to jump to (default: "regulamento-abrolhos")
 * @param {string} [props.ctaLabel]
 */
export default function WinnerSection({
  onCta,
  regulamentoAnchor = "regulamento-abrolhos",
  ctaLabel = "Quero participar dos próximos sorteios",
  variant = "section", // "section" | "modal"
  hideCta = false,
  hideNextPrize = false,
}) {
  const isModal = variant === "modal";
  const Wrapper = isModal ? "div" : "section";
  const wrapperProps = isModal
    ? { className: "relative overflow-hidden bg-[#071B2D] px-5 py-10 sm:py-12" }
    : {
        id: "vencedor",
        className: "relative overflow-hidden bg-[#071B2D] px-5 py-24 sm:py-32",
        "data-testid": "winner-section",
      };

  const handleCta = () => {
    if (onCta) return onCta();
    const target =
      document.getElementById("combos") ||
      document.getElementById("participar") ||
      document.getElementById("sorteio");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToRegulamento = (e) => {
    e.preventDefault();
    document
      .getElementById(regulamentoAnchor)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Wrapper {...wrapperProps}>
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-[#F5C542]/10 blur-3xl" />
        <div className="absolute -right-24 bottom-24 h-80 w-80 rounded-full bg-[#3B82F6]/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 82%, rgba(245,197,66,0.10) 0px, transparent 8px), radial-gradient(circle at 82% 22%, rgba(123,224,214,0.08) 0px, transparent 10px), radial-gradient(circle at 28% 20%, rgba(245,197,66,0.06) 0px, transparent 6px)",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-center"
        >
          <p className="inline-flex items-center gap-2 rounded-full border border-[#F5C542]/40 bg-[#F5C542]/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#F5C542] backdrop-blur">
            <Trophy className="h-3.5 w-3.5" /> Quem ganha de verdade
          </p>
          <h2 className="mt-6 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
            🏆 O primeiro vencedor{" "}
            <span className="text-[#F5C542]">já existe.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Enquanto muita gente ainda estava pensando se entrava no Clube, um dos nossos
            membros já garantiu uma{" "}
            <strong className="font-bold text-white">Expedição para Abrolhos para 2 pessoas</strong>.
          </p>
        </motion.div>

        {/* Winners counters */}
        <div className="mx-auto mt-10 flex max-w-md items-center justify-center gap-4">
          <AnimatedCounter target={1} label="Vencedor oficial" testId="counter-winners" />
          <AnimatedCounter target={1} label="Prêmio entregue" testId="counter-prizes" />
        </div>

        {/* WINNER CARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="relative mt-14 overflow-hidden rounded-3xl border-2 border-[#F5C542]/45 bg-gradient-to-br from-[#F5C542]/[0.08] via-white/[0.04] to-transparent shadow-2xl shadow-[#F5C542]/15 backdrop-blur-md"
          data-testid="winner-card"
        >
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Photo */}
            <div className="group relative aspect-[4/3] w-full overflow-hidden md:aspect-auto">
              <img
                src={WINNER_PHOTO}
                alt="Carlos Eduardo Gerum Carraro Cruz"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 group-hover:brightness-110"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#071B2D] via-[#071B2D]/30 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-[#071B2D]/60" />
              {/* Golden shimmer on hover */}
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#F5C542]/0 via-[#F5C542]/15 to-[#F5C542]/0" />
              </div>
              <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full border border-[#F5C542] bg-[#F5C542] px-3.5 py-2 text-[10px] font-black uppercase tracking-widest text-[#071B2D] shadow-2xl">
                <Award className="h-3.5 w-3.5" strokeWidth={2.5} />
                1º Ganhador Oficial
              </div>
            </div>

            {/* Info */}
            <div className="flex flex-col justify-center gap-4 p-6 sm:p-8 md:p-10">
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#F5C542]">
                Vencedor
              </p>
              <h3 className="font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">
                Carlos Eduardo Gerum Carraro Cruz
              </h3>

              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 sm:p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
                    Nº participante
                  </p>
                  <p className="mt-1 font-mono text-2xl font-black text-white sm:text-3xl">
                    48545
                  </p>
                </div>
                <div className="rounded-xl border border-[#F5C542]/40 bg-[#F5C542]/10 p-3 sm:p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-[#F5C542]">
                    1º prêmio Federal
                  </p>
                  <p className="mt-1 font-mono text-2xl font-black text-[#F5C542] sm:text-3xl">
                    48522
                  </p>
                </div>
              </div>

              <p className="mt-2 text-xs leading-relaxed text-white/60 sm:text-sm">
                Vencedor conforme o regulamento do Clube Dive Master utilizando o número
                imediatamente acima do 1º prêmio da Loteria Federal.
              </p>
            </div>
          </div>
        </motion.div>

        {/* TRANSPARÊNCIA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="mt-10 rounded-3xl border border-emerald-400/25 bg-emerald-500/[0.04] p-6 backdrop-blur-md sm:p-8"
          data-testid="transparency-card"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30">
              <ShieldCheck className="h-6 w-6" strokeWidth={2.3} />
            </span>
            <div className="flex-1">
              <h4 className="font-display text-lg font-black uppercase tracking-tight text-white sm:text-xl">
                🔍 Sorteio realizado com total transparência
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-white/80 sm:text-base">
                O resultado do Clube Dive Master é baseado na{" "}
                <strong className="font-bold text-white">
                  Loteria Federal da Caixa Econômica Federal
                </strong>
                , garantindo um processo público, auditável e imparcial.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
                Concurso
              </p>
              <p className="mt-1 font-mono text-xl font-black text-white sm:text-2xl">
                6086
              </p>
            </div>
            <div className="rounded-2xl border border-[#F5C542]/40 bg-[#F5C542]/10 p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#F5C542]">
                1º prêmio sorteado
              </p>
              <p className="mt-1 font-mono text-xl font-black text-[#F5C542] sm:text-2xl">
                48522
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">
                Participante vencedor
              </p>
              <p className="mt-1 font-mono text-xl font-black text-emerald-300 sm:text-2xl">
                48545
              </p>
            </div>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-500/15 px-4 py-2 text-[11px] font-bold text-emerald-200 sm:text-xs">
            <CheckCircle2 className="h-4 w-4" />
            Resultado validado conforme o regulamento do Clube Dive Master.
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href={LOTTERY_OFFICIAL_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-5 py-2.5 text-xs font-bold text-white/90 backdrop-blur transition-colors hover:border-white/40 hover:bg-white/10 sm:text-sm"
              data-testid="lottery-official-link"
            >
              <ExternalLink className="h-4 w-4" />
              Conferir resultado oficial na Caixa
            </a>
            <button
              type="button"
              onClick={scrollToRegulamento}
              className="text-xs font-semibold text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline sm:text-sm"
              data-testid="see-regulamento-link"
            >
              Ver regulamento completo do Clube Dive Master
            </button>
          </div>
        </motion.div>

        {/* BLOCO EMOCIONAL */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="mt-12 rounded-3xl border-2 border-[#F5C542]/50 bg-gradient-to-br from-[#F5C542]/[0.1] to-transparent p-6 text-center backdrop-blur-md sm:p-10"
        >
          <p className="font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl">
            Hoje foi o Carlos.
          </p>
          <p className="mt-2 font-display text-2xl font-black uppercase leading-tight tracking-tight text-[#F5C542] sm:text-4xl">
            Amanhã pode ser você.
          </p>
        </motion.div>

        {/* PRÓXIMO PRÊMIO */}
        {!hideNextPrize && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          className="relative mt-12 overflow-hidden rounded-3xl border-2 border-[#F5C542] bg-gradient-to-br from-[#3D2F0A] via-[#1A2A5A] to-[#0A1F52] p-6 shadow-2xl shadow-[#F5C542]/25 sm:p-10"
          data-testid="next-prize-card"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#F5C542]/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#3B82F6]/20 blur-3xl" />

          <div className="relative flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-[#F5C542] text-[#04102A] shadow-lg shadow-[#F5C542]/40 sm:h-24 sm:w-24">
              <GraduationCap className="h-11 w-11 sm:h-14 sm:w-14" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#F5C542]">
                🎁 Próximo sorteio
              </p>
              <h3 className="mt-2 font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl">
                2 cursos de mergulho
              </h3>
              <p className="mt-2 text-sm text-white/80 sm:text-base">
                Serão <strong className="font-bold text-[#F5C542]">2 vencedores diferentes</strong>.
                Cada vencedor poderá escolher entre:
              </p>
              <ul className="mt-4 space-y-2 text-sm sm:text-base">
                <li className="flex items-start gap-2 text-white/90">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5C542]" />
                  Open Water Diver
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5C542]" />
                  Advanced Open Water Diver (caso já seja certificado)
                </li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-2 text-[11px] font-bold text-white/70">
                <Lock className="h-3.5 w-3.5" /> Data do próximo sorteio será divulgada em breve
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* CTA */}
        {!hideCta && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="mt-14 flex flex-col items-center gap-4"
        >
          <button
            type="button"
            onClick={handleCta}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F5C542] px-8 py-5 text-base font-extrabold uppercase tracking-wide text-[#04102A] shadow-2xl shadow-[#F5C542]/40 transition-transform hover:scale-[1.02] sm:w-auto sm:text-lg"
            data-testid="winner-section-cta"
          >
            <Sparkles className="h-5 w-5" />
            {ctaLabel}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <p className="mx-auto max-w-xl text-center text-xs leading-relaxed text-white/60 sm:text-sm">
            Você não participa apenas dos sorteios. Também garante{" "}
            <strong className="font-bold text-white">descontos exclusivos durante 1 ano</strong>{" "}
            em cursos, viagens, equipamentos e experiências da Dive Master.
          </p>
        </motion.div>
        )}

        {/* FAIXA FINAL */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="mt-16 overflow-hidden rounded-2xl border border-[#F5C542]/40 bg-gradient-to-r from-[#F5C542]/[0.08] via-white/[0.03] to-[#F5C542]/[0.08] px-6 py-6 text-center backdrop-blur-md sm:py-7"
        >
          <p className="flex flex-wrap items-center justify-center gap-2 font-display text-base font-black uppercase leading-snug tracking-tight text-white sm:text-lg">
            <Waves className="h-5 w-5 text-[#7BE0D6]" />
            O primeiro prêmio já encontrou um dono. O próximo pode ter o seu nome.{" "}
            <span className="text-[#F5C542]">🐋💙</span>
          </p>
        </motion.div>
      </div>
    </Wrapper>
  );
}

/**
 * Modal wrapper for WinnerSection — opens in overlay.
 */
export function WinnerModal({ open, onClose, showNextPrize = true }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-black/85 px-3 py-6 backdrop-blur-md sm:px-6 sm:py-10"
      onClick={onClose}
      data-testid="winner-modal"
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-[#F5C542]/40 bg-[#071B2D] shadow-2xl shadow-black/70"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white/90 backdrop-blur-md transition-colors hover:border-white/40 hover:bg-black/70"
          aria-label="Fechar"
          data-testid="close-winner-modal"
        >
          ✕
        </button>

        <WinnerSection variant="modal" hideCta hideNextPrize={!showNextPrize} />
      </div>
    </div>
  );
}
